import { useState, type ReactNode } from "react";
import { Box } from "@mui/material";
import { LockRounded } from "@mui/icons-material";
import { focusRingSx } from "../overviewPanel";
import { hapticError } from "../../../lib/haptics";
import { isNative } from "../../../lib/platform";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { FeatureLimitModal } from "../../polish/FeatureLimitModal";
import type { TonalTokens } from "../../polish/tokens";

// The lead's plan-neutral wording, for Ben to change (implementation-log.md,
// Fix cycle 1): D2 serves the feed to Basic and Pro, so no plan is named.
export const LOCKED_PILL = "Earnings feed · Members";
export const LOCKED_TITLE =
  "Every report and what analysts did next, with an Insight membership";
export const LOCKED_POINTS = [
  "EPS and revenue against the estimate",
  "The share price move since the report",
  "Upgrades, downgrades and price-target changes after each report",
];

interface EarningsLockedProps {
  tokens: TonalTokens;
  /** The content's shapes, still grey, that the pill sits over. */
  children: ReactNode;
}

/**
 * Web only, a signed-in user without a membership: the content's shapes in
 * still grey with a locked pill that opens the upsell. Native never mounts
 * it (every native user is a member).
 */
export const EarningsLocked = ({ tokens, children }: EarningsLockedProps) => {
  const [open, setOpen] = useState(false);
  const { trackFeature } = useAnalytics();
  return (
    <Box data-testid="earnings-locked" sx={{ position: "relative" }}>
      <Box
        aria-hidden
        sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
      >
        {children}
      </Box>
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => {
            hapticError();
            setOpen(true);
          }}
          sx={{
            all: "unset",
            ...focusRingSx,
            boxSizing: "border-box",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            minHeight: 44,
            px: 2.5,
            borderRadius: "999px",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: tokens.gain,
            backgroundColor: tokens.surface,
            border: "1px solid rgba(34, 197, 94, 0.5)",
            transition: "background-color 0.2s ease",
            "@media (hover: hover)": {
              "&:hover": { backgroundColor: tokens.raised },
            },
          }}
        >
          <LockRounded sx={{ fontSize: 16 }} />
          {LOCKED_PILL}
        </Box>
      </Box>
      <FeatureLimitModal
        open={open}
        canUpgrade={!isNative()}
        onClose={() => setOpen(false)}
        title={LOCKED_TITLE}
        points={LOCKED_POINTS}
        from="earnings"
        onUpgrade={() =>
          trackFeature("earnings_upgrade_clicked", { source: "locked" })
        }
      />
    </Box>
  );
};
